export type FormLayoutHeadingLevel = 'heading' | 'subheading';

export type LayoutFieldItem = { type: 'field'; fieldId: string };
export type LayoutHeadingItem = {
  type: 'heading';
  id: string;
  text: string;
  level: FormLayoutHeadingLevel;
  disabled?: boolean;
};
export type LayoutDividerItem = { type: 'divider'; id: string; disabled?: boolean };
// Where the page's discount code box sits; the box shows only while the page has it switched on.
export type LayoutDiscountCodeItem = { type: 'discount-code'; id: string };
export type LayoutBlockItem = LayoutHeadingItem | LayoutDividerItem | LayoutDiscountCodeItem;
export type LayoutGroupChild = LayoutFieldItem | LayoutBlockItem;
export type LayoutGroupItem = {
  type: 'group';
  id: string;
  name?: string;
  columns: number;
  items: LayoutGroupChild[];
};
export type LayoutItem = LayoutFieldItem | LayoutBlockItem | LayoutGroupItem;
export type FormLayout = LayoutItem[];

type IdLike = string | { toString(): string };
export type LayoutFieldLike = { _id: IdLike; order?: number | null };

export type ResolvedFieldItem<F> = LayoutFieldItem & { field: F };
export type ResolvedGroupChild<F> = ResolvedFieldItem<F> | LayoutBlockItem;
export type ResolvedGroupItem<F> = Omit<LayoutGroupItem, 'items'> & {
  items: ResolvedGroupChild<F>[];
};
export type ResolvedLayoutItem<F> = ResolvedFieldItem<F> | LayoutBlockItem | ResolvedGroupItem<F>;

export const DISCOUNT_CODE_ITEM_ID = 'discount-code';

export const FORM_LAYOUT_COLUMNS = [1, 2, 3, 4] as const;

const idOf = (value: IdLike) => String(value);

/** A heading, divider or discount code as stored: only its own keys, `disabled` only when set. */
function plainBlock(item: LayoutBlockItem): LayoutBlockItem {
  const disabled = 'disabled' in item && item.disabled === true ? { disabled: true } : {};
  if (item.type === 'heading') {
    return { type: 'heading', id: item.id, text: item.text, level: item.level, ...disabled };
  }
  if (item.type === 'divider') return { type: 'divider', id: item.id, ...disabled };
  return { type: 'discount-code', id: item.id };
}

function clampColumns(columns: unknown): number {
  const n = Math.round(Number(columns));
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.min(n, FORM_LAYOUT_COLUMNS.length);
}

function byOrder<F extends LayoutFieldLike>(fields: F[]): F[] {
  return fields
    .map((field, index) => ({ field, index }))
    .sort((a, b) => (a.field.order ?? 0) - (b.field.order ?? 0) || a.index - b.index)
    .map(entry => entry.field);
}

/**
 * The layout as it should be shown, reconciled with the page's live fields: a page with no
 * layout keeps today's order, fields the layout does not list are appended, dead entries go.
 */
export function resolveFormLayout<F extends LayoutFieldLike>(
  layout: FormLayout | null | undefined,
  fields: F[] | null | undefined,
): ResolvedLayoutItem<F>[] {
  const live = fields ?? [];
  const byId = new Map(live.map(field => [idOf(field._id), field]));
  const placed = new Set<string>();
  const resolved: ResolvedLayoutItem<F>[] = [];

  const takeField = (item: LayoutFieldItem): ResolvedFieldItem<F> | null => {
    const id = idOf(item.fieldId);
    const field = byId.get(id);
    if (!field || placed.has(id)) return null;
    placed.add(id);
    return { type: 'field', fieldId: id, field };
  };

  for (const item of Array.isArray(layout) ? layout : []) {
    if (!item || typeof item !== 'object') continue;
    if (item.type === 'field') {
      const taken = takeField(item);
      if (taken) resolved.push(taken);
    } else if (item.type === 'heading') {
      resolved.push(plainBlock(item));
    } else if (item.type === 'divider' || item.type === 'discount-code') {
      resolved.push(plainBlock(item));
    } else if (item.type === 'group') {
      const items: ResolvedGroupChild<F>[] = [];
      for (const child of Array.isArray(item.items) ? item.items : []) {
        if (child?.type === 'field') {
          const taken = takeField(child);
          if (taken) items.push(taken);
        } else if (child?.type === 'heading') {
          items.push(plainBlock(child));
        } else if (child?.type === 'divider' || child?.type === 'discount-code') {
          items.push(plainBlock(child));
        }
      }
      resolved.push({
        type: 'group',
        id: item.id,
        ...(item.name ? { name: item.name } : {}),
        columns: clampColumns(item.columns),
        items,
      });
    }
  }

  for (const field of byOrder(live.filter(field => !placed.has(idOf(field._id))))) {
    resolved.push({ type: 'field', fieldId: idOf(field._id), field });
  }

  return resolved;
}

export function toFormLayout<F>(resolved: ResolvedLayoutItem<F>[]): FormLayout {
  const plainChild = (child: ResolvedGroupChild<F> | ResolvedLayoutItem<F>): LayoutItem => {
    if (child.type === 'field') return { type: 'field', fieldId: child.fieldId };
    if (child.type === 'heading') {
      return plainBlock(child);
    }
    if (child.type === 'divider' || child.type === 'discount-code') {
      return plainBlock(child);
    }
    return {
      type: 'group',
      id: child.id,
      ...(child.name ? { name: child.name } : {}),
      columns: child.columns,
      items: child.items.map(plainChild) as LayoutGroupChild[],
    };
  };
  return resolved.map(plainChild);
}

/** The layout reconciled with the live fields, as plain data. */
export function materializeFormLayout(
  layout: FormLayout | null | undefined,
  fields: LayoutFieldLike[] | null | undefined,
): FormLayout {
  return toFormLayout(resolveFormLayout(layout, fields));
}

/** Field ids in the order a customer sees them, groups walked in place. */
export function flattenLayoutFieldIds(
  layout: FormLayout | ResolvedLayoutItem<unknown>[],
): string[] {
  const ids: string[] = [];
  for (const item of layout) {
    if (item.type === 'field') ids.push(idOf(item.fieldId));
    else if (item.type === 'group') {
      for (const child of item.items) if (child.type === 'field') ids.push(idOf(child.fieldId));
    }
  }
  return ids;
}

export function findGroup(layout: FormLayout, groupId: string): LayoutGroupItem | undefined {
  return layout.find(
    (item): item is LayoutGroupItem => item.type === 'group' && item.id === groupId,
  );
}

export function findFieldGroupId(layout: FormLayout, fieldId: string): string | null {
  for (const item of layout) {
    if (
      item.type === 'group' &&
      item.items.some(child => child.type === 'field' && child.fieldId === fieldId)
    ) {
      return item.id;
    }
  }
  return null;
}

export function removeFieldFromLayout(layout: FormLayout, fieldId: string): FormLayout {
  return layout
    .filter(item => !(item.type === 'field' && item.fieldId === fieldId))
    .map(item =>
      item.type === 'group'
        ? {
            ...item,
            items: item.items.filter(
              child => !(child.type === 'field' && child.fieldId === fieldId),
            ),
          }
        : item,
    );
}

export function appendFieldToLayout(
  layout: FormLayout,
  fieldId: string,
  groupId?: string | null,
): FormLayout {
  const entry: LayoutFieldItem = { type: 'field', fieldId };
  if (!groupId) return [...layout, entry];
  return layout.map(item =>
    item.type === 'group' && item.id === groupId
      ? { ...item, items: [...item.items, entry] }
      : item,
  );
}

/** Puts a new field right after another one, in the same group. Appends when the anchor is missing. */
export function insertFieldAfter(
  layout: FormLayout,
  afterFieldId: string,
  fieldId: string,
): FormLayout {
  const entry: LayoutFieldItem = { type: 'field', fieldId };
  const topIndex = layout.findIndex(item => item.type === 'field' && item.fieldId === afterFieldId);
  if (topIndex !== -1)
    return [...layout.slice(0, topIndex + 1), entry, ...layout.slice(topIndex + 1)];

  let inserted = false;
  const next = layout.map(item => {
    if (item.type !== 'group') return item;
    const index = item.items.findIndex(
      child => child.type === 'field' && child.fieldId === afterFieldId,
    );
    if (index === -1) return item;
    inserted = true;
    return {
      ...item,
      items: [...item.items.slice(0, index + 1), entry, ...item.items.slice(index + 1)],
    };
  });
  return inserted ? next : [...layout, entry];
}

export function ungroupInLayout(layout: FormLayout, groupId: string): FormLayout {
  return layout.flatMap(item =>
    item.type === 'group' && item.id === groupId ? item.items : [item],
  );
}

// Fields in `keepFieldIds` survive, moved to where the group was.
export function removeGroupFromLayout(
  layout: FormLayout,
  groupId: string,
  keepFieldIds: string[] = [],
): { layout: FormLayout; removedFieldIds: string[] } {
  const keep = new Set(keepFieldIds);
  const removedFieldIds: string[] = [];
  const next = layout.flatMap(item => {
    if (item.type !== 'group' || item.id !== groupId) return [item];
    const kept: LayoutItem[] = [];
    for (const child of item.items) {
      if (child.type !== 'field') continue;
      if (keep.has(child.fieldId)) kept.push(child);
      else removedFieldIds.push(child.fieldId);
    }
    return kept;
  });
  return { layout: next, removedFieldIds };
}

/** Inserts an item straight after the group with the given id. Appends when it is missing. */
export function insertAfterGroup(
  layout: FormLayout,
  groupId: string,
  item: LayoutItem,
): FormLayout {
  const index = layout.findIndex(entry => entry.type === 'group' && entry.id === groupId);
  if (index === -1) return [...layout, item];
  return [...layout.slice(0, index + 1), item, ...layout.slice(index + 1)];
}

export function newLayoutItemId(): string {
  const cryptoApi = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

/** "Name (copy)" for a duplicate; an empty name stays empty. */
export function duplicateName(name: string | null | undefined): string | undefined {
  const trimmed = (name ?? '').trim();
  return trimmed ? `${trimmed} (copy)` : undefined;
}

// For the old flat reorder route: every group and the top level keep their slots, and only
// the fields inside each are re-sorted.
export function reorderFieldsInLayout(layout: FormLayout, orderedFieldIds: string[]): FormLayout {
  const rank = new Map(orderedFieldIds.map((id, index) => [String(id), index]));
  const sortSlots = <T extends LayoutItem>(items: T[]): T[] => {
    const fieldItems = items
      .filter((item): item is T & LayoutFieldItem => item.type === 'field')
      .sort((a, b) => (rank.get(a.fieldId) ?? Infinity) - (rank.get(b.fieldId) ?? Infinity));
    let next = 0;
    return items.map(item => (item.type === 'field' ? fieldItems[next++] : item));
  };
  return sortSlots(layout).map(item =>
    item.type === 'group' ? { ...item, items: sortSlots(item.items) } : item,
  );
}

// For a copied page: groups, headings and dividers get new ids, and fields with no copy are dropped.
// The discount code keeps its fixed id: the editor finds the box by it.
export function remapFormLayout(
  layout: FormLayout,
  fieldIdMap: Map<string, string>,
  makeId: () => string = newLayoutItemId,
): FormLayout {
  const remapChild = (child: LayoutGroupChild): LayoutGroupChild | null => {
    if (child.type === 'discount-code') return { ...child, id: DISCOUNT_CODE_ITEM_ID };
    if (child.type !== 'field') return { ...child, id: makeId() };
    const copy = fieldIdMap.get(String(child.fieldId));
    return copy ? { type: 'field', fieldId: copy } : null;
  };
  return layout.flatMap((item): LayoutItem[] => {
    if (item.type !== 'group') {
      const remapped = remapChild(item);
      return remapped ? [remapped] : [];
    }
    return [
      {
        ...item,
        id: makeId(),
        items: item.items
          .map(remapChild)
          .filter((child): child is LayoutGroupChild => child !== null),
      },
    ];
  });
}

export const ROOT_CONTAINER = 'root';

export function layoutItemKey(item: LayoutItem | ResolvedLayoutItem<unknown>): string {
  return item.type === 'field' ? `field:${item.fieldId}` : `${item.type}:${item.id}`;
}

function matchesKey(item: LayoutItem, key: string) {
  return layoutItemKey(item) === key;
}

export function findLayoutItem(
  layout: FormLayout,
  key: string,
): { item: LayoutItem; containerId: string; index: number } | null {
  const top = layout.findIndex(item => matchesKey(item, key));
  if (top !== -1) return { item: layout[top], containerId: ROOT_CONTAINER, index: top };
  for (const item of layout) {
    if (item.type !== 'group') continue;
    const index = item.items.findIndex(child => matchesKey(child, key));
    if (index !== -1) return { item: item.items[index], containerId: item.id, index };
  }
  return null;
}

// Copies a heading or divider right after itself. A page has one discount code box at most.
export function duplicateLayoutBlock(
  layout: FormLayout,
  key: string,
  makeId: () => string = newLayoutItemId,
): { layout: FormLayout; id: string } | null {
  const found = findLayoutItem(layout, key);
  if (!found || ['field', 'group', 'discount-code'].includes(found.item.type)) return null;
  const id = makeId();
  const copy =
    found.item.type === 'heading'
      ? {
          ...found.item,
          id,
          text: (duplicateName(found.item.text) ?? found.item.text).slice(0, 200),
        }
      : { ...found.item, id };
  const insert = <T>(items: T[]) => [
    ...items.slice(0, found.index + 1),
    copy as T,
    ...items.slice(found.index + 1),
  ];
  if (found.containerId === ROOT_CONTAINER) return { layout: insert(layout), id };
  return {
    layout: layout.map(item =>
      item.type === 'group' && item.id === found.containerId
        ? { ...item, items: insert(item.items) }
        : item,
    ),
    id,
  };
}

function removeByKey(layout: FormLayout, key: string): FormLayout {
  return layout
    .filter(item => !matchesKey(item, key))
    .map(item =>
      item.type === 'group'
        ? { ...item, items: item.items.filter(child => !matchesKey(child, key)) }
        : item,
    );
}

// The index counts positions after the item has left its old place. A group never goes into
// a group: such a move is ignored.
export function moveLayoutItem(
  layout: FormLayout,
  key: string,
  target: { containerId: string; index: number },
): FormLayout {
  const found = findLayoutItem(layout, key);
  if (!found) return layout;
  const { item } = found;
  if (item.type === 'group' && target.containerId !== ROOT_CONTAINER) return layout;

  const without = removeByKey(layout, key);
  const clampInsert = <T>(list: T[], entry: T) => {
    const index = Math.max(0, Math.min(target.index, list.length));
    return [...list.slice(0, index), entry, ...list.slice(index)];
  };

  if (target.containerId === ROOT_CONTAINER) return clampInsert(without, item);
  if (!findGroup(without, target.containerId)) return layout;
  return without.map(entry =>
    entry.type === 'group' && entry.id === target.containerId
      ? { ...entry, items: clampInsert(entry.items, item as LayoutGroupChild) }
      : entry,
  );
}

export function addLayoutItem(
  layout: FormLayout,
  item: LayoutItem,
  groupId?: string | null,
): FormLayout {
  if (!groupId) return [...layout, item];
  if (item.type === 'group') return layout;
  return layout.map(entry =>
    entry.type === 'group' && entry.id === groupId
      ? { ...entry, items: [...entry.items, item] }
      : entry,
  );
}

// Only a top-level field can be wrapped: groups never nest.
export function wrapFieldInGroup(
  layout: FormLayout,
  fieldId: string,
  group: Omit<LayoutGroupItem, 'type' | 'items'>,
): FormLayout {
  const index = layout.findIndex(item => item.type === 'field' && item.fieldId === fieldId);
  if (index === -1) return layout;
  const wrapped: LayoutGroupItem = {
    type: 'group',
    ...group,
    items: [layout[index] as LayoutGroupChild],
  };
  return [...layout.slice(0, index), wrapped, ...layout.slice(index + 1)];
}

export function updateGroupInLayout(
  layout: FormLayout,
  groupId: string,
  changes: { name?: string | null; columns?: number },
): FormLayout {
  return layout.map(item => {
    if (item.type !== 'group' || item.id !== groupId) return item;
    const next: LayoutGroupItem = { ...item };
    if (changes.columns !== undefined) next.columns = clampColumns(changes.columns);
    if (changes.name !== undefined) {
      const name = (changes.name ?? '').trim();
      if (name) next.name = name;
      else delete next.name;
    }
    return next;
  });
}

export function updateHeadingInLayout(
  layout: FormLayout,
  headingId: string,
  changes: { text?: string; level?: FormLayoutHeadingLevel },
): FormLayout {
  const update = <T extends LayoutItem>(item: T): T =>
    item.type === 'heading' && item.id === headingId ? ({ ...item, ...changes } as T) : item;
  return layout.map(item =>
    item.type === 'group' ? { ...item, items: item.items.map(update) } : update(item),
  );
}

export function setBlockDisabledInLayout(
  layout: FormLayout,
  key: string,
  disabled: boolean,
): FormLayout {
  const update = <T extends LayoutItem>(item: T): T => {
    if ((item.type !== 'heading' && item.type !== 'divider') || `${item.type}:${item.id}` !== key) {
      return item;
    }
    const { disabled: _was, ...rest } = item;
    return (disabled ? { ...rest, disabled: true } : rest) as T;
  };
  return layout.map(item =>
    item.type === 'group' ? { ...item, items: item.items.map(update) } : update(item),
  );
}

export function removeHeadingFromLayout(layout: FormLayout, headingId: string): FormLayout {
  return removeByKey(layout, `heading:${headingId}`);
}

export function removeDividerFromLayout(layout: FormLayout, dividerId: string): FormLayout {
  return removeByKey(layout, `divider:${dividerId}`);
}

export function layoutHasDiscountCode(layout: FormLayout | null | undefined): boolean {
  return (Array.isArray(layout) ? layout : []).some(
    item =>
      item?.type === 'discount-code' ||
      (item?.type === 'group' && item.items?.some(child => child?.type === 'discount-code')),
  );
}

export function removeDiscountCodeFromLayout(layout: FormLayout): FormLayout {
  return layout
    .filter(item => item.type !== 'discount-code')
    .map(item =>
      item.type === 'group'
        ? { ...item, items: item.items.filter(child => child.type !== 'discount-code') }
        : item,
    );
}

/** Why a field cannot be duplicated, or null: the customer email, Quantity and Address are one per page. */
export function duplicateBlockedReason(field: {
  type?: unknown;
  element?: unknown;
  [key: string]: unknown;
}): string | null {
  if (field.type === 'email') return "The customer email field can't be duplicated";
  if (field.element === 'quantity') return 'A page can only have one Quantity field';
  if (field.element === 'address') return 'A page can only have one Address field';
  return null;
}
